import { z } from 'zod';
import { IsoTime, Labels, ServerId } from './basics.js';
/**
 * Metrics as a chart reads them (knowledge doc section 15: "Time series by server,
 * name, labels, range").
 *
 * `GuardMetric` in `metric.ts` is one reading. This is the other end: readings
 * bucketed by time and grouped by label set, so a request for `disk_used_pct` comes
 * back as one line per mount rather than as ten thousand points the browser has to
 * sort out.
 *
 * Bucketing is the API's job rather than the browser's for two reasons. The raw
 * table is the largest in section 16, so the row count has to be bounded before it
 * reaches a response; and section 8.3 says the growth rate and the "full in N days"
 * estimate are calculated centrally, which needs the same aggregate anyway.
 */
/** Distinct label sets in one answer. Past this the chart is unreadable regardless. */
export const METRIC_SERIES_MAX = 25;
/** Buckets per series. The API widens the bucket rather than truncating the range. */
export const METRIC_POINTS_MAX = 500;
export const METRIC_BUCKET_MIN_SECONDS = 60;
export const METRIC_BUCKET_MAX_SECONDS = 86_400;
export const METRIC_BUCKET_DEFAULT_SECONDS = 300;
/**
 * Raw metrics are kept 30 days (section 16), so a wider range would read as empty
 * rather than as missing. `metrics_hourly` is what extends this to a year, and
 * nothing rolls up into it yet.
 */
export const METRIC_RANGE_MAX_DAYS = 30;
export const METRIC_RANGE_DEFAULT_HOURS = 24;
/** One bucket. `min` and `max` are kept so a chart can show the band, not just the line. */
export const MetricPoint = z.object({
    /** The bucket's start, not its middle. */
    at: IsoTime,
    avg: z.number(),
    min: z.number(),
    max: z.number(),
    samples: z.number().int().positive(),
});
/**
 * Section 8.3: "The API calculates growth rate and 'full in N days' estimates."
 *
 * A least-squares fit over the buckets rather than last-minus-first, because one
 * `du` scan that ran while a backup was on disk should not become a forecast. The
 * fields say what they are in the metric's own unit, so the same shape serves
 * `disk_used_pct` (per cent per day) and `db_size_bytes` (bytes per day).
 */
export const MetricTrend = z.object({
    /** Slope of the fitted line, in the metric's unit per day. */
    changePerDay: z.number(),
    /** Buckets the fit used. Below three there is no trend at all, only two points. */
    samples: z.number().int().nonnegative(),
    /**
     * Days until the series reaches `trend_target`, or null when the query named no
     * target, the slope points away from it, or the answer is so far out that saying
     * it would be false precision.
     */
    reachesTargetInDays: z.number().nullable(),
});
export const MetricSeries = z.object({
    name: z.string().min(1).max(100),
    /** The label set this line is for: `{ mount: '/', fstype: 'ext4' }`. */
    labels: Labels,
    points: z.array(MetricPoint),
    /** Null when there are too few buckets to fit a line through. */
    trend: MetricTrend.nullable(),
});
export const MetricSeriesQuery = z
    .object({
    /**
     * Required. A fleet-wide series would have to decide what averaging across
     * machines means, and section 15 asks for "by server" — the overview's
     * cross-fleet numbers are a different question with a different shape.
     */
    server_id: ServerId,
    name: z
        .string()
        .regex(/^[a-z]+(_[a-z0-9]+)+$/, 'must be snake_case as <area>_<thing>_<unit>')
        .max(100),
    /** Defaults to the last day, ending now. */
    from: IsoTime.optional(),
    to: IsoTime.optional(),
    /** A request, not a promise: the API widens it to keep the point count bounded. */
    bucket_seconds: z.coerce
        .number()
        .int()
        .min(METRIC_BUCKET_MIN_SECONDS)
        .max(METRIC_BUCKET_MAX_SECONDS)
        .default(METRIC_BUCKET_DEFAULT_SECONDS),
    /** The value "full" means for this metric: 100 for a percentage, a cap in bytes. */
    trend_target: z.coerce.number().optional(),
})
    .refine((query) => query.from === undefined ||
    query.to === undefined ||
    Date.parse(query.from) < Date.parse(query.to), { message: '`from` must be before `to`', path: ['from'] });
export const MetricSeriesResponse = z.object({
    serverId: ServerId,
    name: z.string().min(1).max(100),
    /** The range actually used, after the defaults and the 30-day bound. */
    from: IsoTime,
    to: IsoTime,
    /** The bucket actually used, which may be wider than the one that was asked for. */
    bucketSeconds: z.number().int().positive(),
    series: z.array(MetricSeries).max(METRIC_SERIES_MAX),
    /**
     * True when more label sets exist than were returned, so the chart can say "the
     * 25 largest" instead of quietly implying it is showing everything.
     */
    truncated: z.boolean(),
});
//# sourceMappingURL=metric-series.js.map